import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useWhatsApp } from "@/lib/siteSettings";
import { SEO } from "@/lib/seo";

type ServiceKey = "buy" | "rent" | "management";

const BROWSE_TARGET: Record<ServiceKey, string | null> = {
  buy: "/listings?type=sale",
  rent: "/listings?type=rent",
  management: null,
};

const ServiceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { buildWaUrl } = useWhatsApp();

  if (!slug || !["buy", "rent", "management"].includes(slug)) {
    return <Navigate to="/services" replace />;
  }
  const key = slug as ServiceKey;
  const base = `servicePages.${key}`;

  const steps = t(`${base}.steps`, { returnObjects: true }) as Array<{ n: string; title: string; desc: string }>;
  const checks = t(`${base}.checks`, { returnObjects: true }) as string[];
  const audience = key === "management" ? (t(`${base}.audience`, { returnObjects: true }) as Array<{ icon: string; text: string }>) : [];
  const faq = key === "management" ? (t(`${base}.faq`, { returnObjects: true }) as Array<{ q: string; a: string }>) : [];
  const waUrl = buildWaUrl(t(`${base}.whatsapp`));
  const browseTo = BROWSE_TARGET[key];

  return (
    <div className="bg-background">
      <SEO
        title={t(`${base}.seo.title`)}
        description={t(`${base}.seo.description`)}
        path={`/services/${key}`}
      />

      {/* Hero */}
      <section className="bg-hero-gradient px-4 py-16 sm:px-6 lg:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-balance text-4xl font-black tracking-tight sm:text-5xl">
            {t(`${base}.title`)}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">
            {t(`${base}.subtitle`)}
          </p>
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-base font-black text-primary-foreground shadow-premium transition hover:scale-[1.02]"
          >
            <MessageCircle className="h-5 w-5" /> {t("services.cta.inquireNow")}
          </a>
        </div>
      </section>

      {/* Audience (management only) */}
      {audience.length > 0 && (
        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-center text-2xl font-black sm:text-3xl">{t(`${base}.audienceTitle`)}</h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {audience.map((a, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft">
                  <div className="text-3xl">{a.icon}</div>
                  <p className="mt-3 text-sm leading-7 text-foreground">{a.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Steps */}
      <section className="bg-card/30 px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-2xl font-black sm:text-3xl">{t("servicePages.common.stepsTitle")}</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div key={i} className="relative rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-1 hover:border-primary/60 hover:shadow-premium">
                <span className="text-sm font-black text-primary">{s.n}</span>
                <h3 className="mt-2 text-lg font-black">{s.title}</h3>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Checks */}
      <section className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-2xl font-black sm:text-3xl">{t("servicePages.common.offerTitle")}</h2>
          <ul className="mx-auto mt-8 grid max-w-3xl gap-3 sm:grid-cols-2">
            {checks.map((c, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm leading-6">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ (management only) */}
      {faq.length > 0 && (
        <section className="bg-card/30 px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-center text-2xl font-black sm:text-3xl">{t("servicePages.common.faqTitle")}</h2>
            <Accordion type="single" collapsible className="mt-8">
              {faq.map((f, i) => (
                <AccordionItem key={i} value={`q${i}`} className="border-border">
                  <AccordionTrigger className="text-start text-base font-bold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-7 text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-8 text-center shadow-premium sm:p-10">
          <h2 className="text-2xl font-black sm:text-3xl">{t(`${base}.bottomCta.title`)}</h2>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {browseTo && (
              <Link
                to={browseTo}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-bold text-foreground transition hover:border-primary hover:scale-[1.02]"
              >
                {t(`${base}.bottomCta.browseLabel`)} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            )}
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-black text-primary-foreground shadow-premium transition hover:scale-[1.02]"
            >
              <MessageCircle className="h-4 w-4" /> {t(`${base}.bottomCta.waLabel`)}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
